/* eslint-disable no-unused-vars */
import { MessageOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button as AntdButton, Col, Divider, Input, List, Row, Spin, Tag } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Loader from "@ui/feedback/loader";
import Button from "@ui/form-controls/button";
import { getAppHistory, getSmsHistory, sendSMS } from "@redux/actionCreators";
import { SMSCreditCounter } from "@ui/messaging/sms-credit-counter";
import "./communicationHistory.scss";
import { getParams } from "utils/reusableFunctions";

const { TextArea } = Input;

const asMessageList = value => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value && typeof value === "object" && ("Content" in value || "TimeStamp" in value)) {
    return [value];
  }
  return [];
};

const messageTime = item => {
  const time = moment(item?.TimeStamp).valueOf();
  return Number.isFinite(time) ? time : 0;
};

const ChannelChip = ({ channel }) => (
  <Tag color={channel === "app" ? "green" : "blue"} className="!m-0">
    {channel === "app" ? "App" : "SMS"}
  </Tag>
);

const SmsHistory = ({ siteData, messageObject, refresh, layout, headerLeft = null }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    personObjectId = "",
    candidateObjectId = "",
    ObjectId: siteObjectId = ""
  } = getParams(location.search);

  const [smsContent, setSmsContent] = useState("");
  const [smsCharCount, setSmsCharCount] = useState(0);

  const reducerDriverData = useSelector(state => state.driverReducer || null, shallowEqual);

  const {
    smsHistoryList: { isLoading: smsLoading = false, resultData: smsResult } = {},
    getAppHistoryInfo: { isLoading: appLoading = false, resultData: appResult } = {},
    sendSMSInfo
  } = reducerDriverData;

  const appPersonId = messageObject?.person || personObjectId;
  const shouldLoadApp = !siteData && Boolean(appPersonId);
  const isLoading = smsLoading || (shouldLoadApp && appLoading);

  const getSiteSmsHistoryUrl = () => "agencyUser/siteManagment/getSiteSMSHistory?siteObjectId=";
  const getHumanSmsHistoryUrl = () => {
    return `agencyUser/driverManagment/getSMSHistory?${"candidateObjectId="}`;
  };

  const loadMessages = () => {
    dispatch(
      getSmsHistory({
        ObjectId:
          messageObject?.candidate || messageObject?.person || siteObjectId || candidateObjectId,
        endPoint: siteData ? getSiteSmsHistoryUrl() : getHumanSmsHistoryUrl()
      })
    );
    if (shouldLoadApp) {
      dispatch(getAppHistory(appPersonId));
    }
  };

  const saveData = () => {
    const payloadData = {
      ObjectId: messageObject?.person || siteObjectId || personObjectId,
      endPoint: siteData
        ? "agencyUser/siteManagment/sendSiteSMSReply?siteObjectId="
        : "agencyUser/driverManagment/sendSMS?personObjectId=",
      smsContent
    };

    dispatch(sendSMS(payloadData));
    setSmsCharCount(0);
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const historyPersonId = messageObject?.person || "";
  const historyCandidateId = messageObject?.candidate || "";

  useEffect(() => {
    if (historyCandidateId || historyPersonId) {
      loadMessages();
      setSmsContent("");
    }
  }, [historyCandidateId, historyPersonId]);

  useEffect(() => {
    if (sendSMSInfo?.resultData) {
      setTimeout(() => {
        loadMessages();
      }, 1000);
      setSmsContent("");
    }
  }, [sendSMSInfo]);

  useEffect(() => {
    setTimeout(() => {
      loadMessages();
    }, 1000);
    setSmsContent("");
  }, [refresh]);

  const smsMessages = asMessageList(
    smsResult?.Data || smsResult?.msg_ListMessagesResult?.Messages?.msgMessageItem
  ).map(item => ({ ...item, channel: "sms" }));
  const appMessages = shouldLoadApp
    ? asMessageList(appResult?.Data).map(item => ({ ...item, channel: "app" }))
    : [];
  const messages = [...smsMessages, ...appMessages].sort((a, b) => messageTime(b) - messageTime(a));

  const historyToolbar = (
    <div className={`mb-2 flex items-center gap-2 ${headerLeft ? "" : "justify-end"}`}>
      {headerLeft ? <div className="min-w-0 flex-1">{headerLeft}</div> : null}
      <Button variant="outline" size="sm" onClick={loadMessages}>
        <ReloadOutlined />
        Refresh
      </Button>
    </div>
  );

  if (layout === "panel") {
    return (
      <div className="driver-details-main-sms">
        <Spin
          indicator={<Loader />}
          spinning={isLoading || sendSMSInfo.isLoading}
          className="loaderGifPosition"
        >
          {historyToolbar}

          <div className="max-h-80 overflow-y-auto rounded border border-border p-3">
            {messages.length === 0 ? (
              <p className="m-0 text-sm text-text-muted">No messages</p>
            ) : (
              <div className="flex flex-col gap-3">
                {messages.map((item, index) => {
                  const inbound = Boolean(item?.Inbound);
                  return (
                    <div
                      key={`${item.channel}-${item?.TimeStamp || "message"}-${index}`}
                      className={inbound ? "flex flex-col items-start" : "flex flex-col items-end"}
                    >
                      <div
                        className={
                          inbound
                            ? "max-w-[80%] whitespace-pre-wrap rounded-lg bg-gray-200 px-3 py-2 text-sm text-text-default"
                            : "max-w-[80%] whitespace-pre-wrap rounded-lg bg-brand/10 px-3 py-2 text-sm text-text-default"
                        }
                      >
                        {item?.Content}
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 text-xs text-text-muted">
                        <ChannelChip channel={item.channel} />
                        {moment(item?.TimeStamp).format("DD-MM-YYYY HH:mm")}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Spin>
      </div>
    );
  }

  return (
    <div className="driver-details-main-sms">
      <Spin
        indicator={<Loader />}
        spinning={isLoading || sendSMSInfo.isLoading}
        className="loaderGifPosition"
      >
        <>
          <Row gutter={[16, { xs: 8, sm: 16, md: 24, lg: 32 }]}>
            <Col span={18}>
              <TextArea
                rows={5}
                className="input"
                value={smsContent}
                maxLength={1224}
                onChange={e => {
                  setSmsContent(e.target.value);
                  setSmsCharCount(e.target.value?.length || 0);
                }}
              />
              <SMSCreditCounter charCount={smsCharCount} />
            </Col>
            <Col span={4}>
              <AntdButton
                className="send"
                style={{ margin: "0 8px" }}
                disabled={!smsContent}
                onClick={() => saveData()}
              >
                <MessageOutlined className="status-icons" />
                Send
              </AntdButton>
            </Col>
          </Row>
          <Divider />
          {historyToolbar}
          <List
            bordered
            dataSource={messages}
            renderItem={item => (
              <List.Item
                style={{
                  background: item?.Inbound ? "#c7c7c7" : "none"
                }}
              >
                <List.Item.Meta description={item?.Content} />
                <div className="ml-[5px] flex items-center gap-2">
                  <ChannelChip channel={item.channel} />
                  {moment(item?.TimeStamp).format("DD-MM-YYYY HH:mm")}
                </div>
              </List.Item>
            )}
            style={{
              height: "300px",
              overflow: "scroll"
            }}
          />
        </>
      </Spin>
    </div>
  );
};

export default SmsHistory;
